<template>
  <div class="fts-settings-compare" v-if="plans.length">
    <!-- What fits you: the workspace's usage placed on every rung, so the ladder and the
         workspace's position on it are one graphic rather than a table to read. -->
    <section class="fts-settings-card" v-if="ruler">
      <div>
        <h2>What fits you</h2>
        <p class="fts-settings-card-sub">
          What this workspace uses, placed on every plan. Each marker sits inside the smallest plan
          that covers that row.
        </p>
      </div>

      <div class="fts-settings-ruler">
        <div class="fts-settings-ruler-side">
          <div class="fts-settings-ruler-row" v-for="row in ruler.rows" :key="row.key">
            <div class="fts-settings-ruler-label">
              <font-awesome-icon :icon="['fas', row.icon]" />
              <span>{{ row.label }}</span>
            </div>
            <div class="fts-settings-ruler-used">{{ row.used }}</div>
          </div>
        </div>

        <div class="fts-settings-ruler-track">
          <div class="fts-settings-ruler-heads">
            <span
              v-for="col in ruler.columns"
              :key="col.planKey"
              class="fts-settings-ruler-head"
              :class="{ 'is-current': col.current }"
            >
              {{ col.name }}
            </span>
          </div>

          <div class="fts-settings-ruler-row" v-for="row in ruler.rows" :key="row.key">
            <div class="fts-settings-ruler-bar">
              <div class="fts-settings-ruler-fill" :style="{ width: row.fill }"></div>
              <span class="fts-settings-ruler-marker" :style="{ left: row.fill }"></span>
            </div>
            <div class="fts-settings-ruler-ticks">
              <span v-for="(tick, i) in row.ticks" :key="i">{{ tick }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="fts-settings-banner" :class="verdict.tone === 'warn' ? 'is-warn' : ''">
        <font-awesome-icon :icon="['fas', verdict.tone === 'warn' ? 'exclamation-triangle' : 'check']" />
        <span>
          <strong>{{ verdict.title }}</strong>
          {{ verdict.body }}
        </span>
      </div>
    </section>

    <!-- Plans stay visible after subscribing (W-8), each stating its fit. A cheaper fit is
         stated here and nowhere else: never an attention item, a banner or an email. -->
    <section class="fts-settings-card">
      <div>
        <h2>Plans</h2>
        <p class="fts-settings-card-sub">
          Every plan, whether or not you are on it. Prices exclude tax.
        </p>
      </div>

      <div class="fts-settings-plans">
        <div
          class="fts-settings-plan"
          v-for="plan in plans"
          :key="plan.planKey"
          :class="{ 'is-current': plan.current }"
        >
          <div class="fts-settings-plan-head">
            <h3>{{ plan.name }}</h3>
            <span class="fts-settings-plan-tag" v-if="plan.current">Current plan</span>
          </div>

          <div class="fts-settings-plan-price">
            <span class="fts-settings-plan-amount">{{ plan.price }}</span>
            <span class="fts-settings-plan-per">{{ plan.per }}</span>
          </div>

          <div class="fts-settings-plan-fit" :class="plan.fit.tone === 'warn' ? 'is-warn' : 'is-ok'">
            <font-awesome-icon :icon="['fas', plan.fit.tone === 'warn' ? 'exclamation-triangle' : 'check']" />
            <span>{{ plan.fit.text }}</span>
          </div>

          <dl class="fts-settings-plan-feats">
            <div v-for="feat in plan.feats" :key="feat.label">
              <dt>{{ feat.label }}</dt>
              <dd>{{ feat.value }}</dd>
            </div>
          </dl>

          <!-- No live subscription (SS06203, SS06209): a plan is bought from its own card. The
               names carry the plan, so each card's Subscribe is the only one with that name. -->
          <div class="fts-settings-plan-buy" v-if="subscribe && plan.sellable">
            <button
              type="button"
              class="fts-settings-btn is-primary"
              :aria-label="`Subscribe monthly to ${plan.name}`"
              @click="$emit('subscribe', { planKey: plan.planKey, interval: 'month' })"
            >
              Subscribe monthly
            </button>
            <button
              type="button"
              class="fts-settings-btn"
              :aria-label="`Pay yearly for ${plan.name}`"
              v-if="plan.yearly"
              @click="$emit('subscribe', { planKey: plan.planKey, interval: 'year' })"
            >
              Pay yearly
            </button>
          </div>
        </div>
      </div>

      <p class="fts-settings-note">
        A limit never stops your work: people can always create, update and close records. Invites
        and uploads pause at their limits. AI is bought separately and no plan includes a monthly
        allowance.
      </p>
    </section>
  </div>
</template>

<script>
/**
 * Settings › Plan — the comparison (W-7, W-8).
 *
 * Every number here comes from `summary.plans[]`, which the API resolves from the plan
 * catalogue: the page carries no copy of the price list. The free rung is synthesised
 * server-side and arrives marked `sellable: false`.
 */
const ORDER = ["free", "essential", "team", "business"];

export default {
  name: "SettingsPlanCompare",
  props: {
    summary: Object,
    // No live subscription: each sellable plan's card offers to buy it (SettingsPlan routes the
    // choice to Stripe Checkout).
    subscribe: Boolean,
  },
  computed: {
    currentKey() {
      return (this.summary && this.summary.planKey) || "free";
    },
    plans() {
      const raw = (this.summary && this.summary.plans) || [];
      const known = raw
        .slice()
        .sort((a, b) => this.rank(a.planKey) - this.rank(b.planKey));
      return known.map(p => this.decorate(p));
    },
    capacity() {
      return this.summary && this.summary.capacity;
    },
    /**
     * Three rows — people, records, storage — each laid across the rungs. A row's marker sits
     * at the workspace's usage, scaled so every rung takes an equal slice of the track: the
     * ladder's steps are orders of magnitude apart, so a linear axis would put every workspace
     * at the far left and say nothing.
     */
    ruler() {
      const c = this.capacity;
      if (!c || !this.plans.length) return null;
      const rows = [
        this.row("people", "People", "user-friends", c.people.used, p => p.raw.includedMembers, v => this.number(v)),
        this.row("records", "Active records", "table-cells-large", c.activeRecords.used, p => p.raw.activeRecords, v => this.number(v)),
        this.row("storage", "File storage", "server", c.storageGb.used, p => p.raw.storageGb, v => `${this.number(v)} GB`),
      ].filter(Boolean);
      if (!rows.length) return null;
      return {
        columns: this.plans.map(p => ({ planKey: p.planKey, name: p.name, current: p.current })),
        rows,
      };
    },
    /** The dimension that decides which rung fits, named plainly. */
    verdict() {
      const c = this.capacity;
      if (!c) return { tone: "ok", title: "", body: "" };
      const over = this.plans
        .filter(p => p.current)
        .map(p => p.overBy)[0] || [];
      if (over.length) {
        return {
          tone: "warn",
          title: `Over what ${this.planName(this.currentKey)} includes on ${this.listOf(over)}.`,
          body: "Nothing is blocked. Invites and uploads pause at their limits; records keep working.",
        };
      }
      const fits = this.plans.find(p => p.sellableOrFree && !p.overBy.length);
      if (!fits) return { tone: "ok", title: "", body: "" };
      const same = fits.planKey === this.currentKey;
      return {
        tone: "ok",
        title: same
          ? `${fits.name} is the smallest plan that fits.`
          : `${fits.name} is the smallest plan that would fit.`,
        body: this.headroom(this.plans.find(p => p.current) || fits),
      };
    },
  },
  methods: {
    rank(key) {
      const i = ORDER.indexOf(String(key || "").toLowerCase());
      return i < 0 ? ORDER.length : i;
    },
    planName(key) {
      const k = String(key || "free");
      return k.charAt(0).toUpperCase() + k.slice(1);
    },
    number(value) {
      const n = Number(value || 0);
      return n >= 100 ? Math.round(n).toLocaleString("en-US") : (Math.round(n * 10) / 10).toString();
    },
    money(value) {
      const symbols = { usd: "US$", sgd: "S$", myr: "RM" };
      const currency = (this.summary && this.summary.currency) || "usd";
      const symbol = symbols[String(currency).toLowerCase()] || `${String(currency).toUpperCase()} `;
      const n = Number(value);
      if (!isFinite(n) || n <= 0) return "";
      const whole = Math.abs(n - Math.round(n)) < 0.005;
      return symbol + n.toLocaleString("en-US", {
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2,
      });
    },
    listOf(words) {
      if (words.length === 1) return words[0];
      return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
    },
    /** Which dimensions this workspace is over, for one plan. */
    overFor(plan) {
      const c = this.capacity;
      if (!c) return [];
      const out = [];
      if (plan.includedMembers != null && c.people.used > plan.includedMembers) out.push("people");
      if (plan.activeRecords != null && c.activeRecords.used > plan.activeRecords) out.push("records");
      if (plan.storageGb != null && c.storageGb.used > plan.storageGb) out.push("storage");
      return out;
    },
    headroom(plan) {
      const c = this.capacity;
      if (!c || !plan || plan.raw.includedMembers == null) return "";
      const people = plan.raw.includedMembers - c.people.used;
      const records = plan.raw.activeRecords != null
        ? plan.raw.activeRecords - c.activeRecords.used
        : null;
      if (people < 0 || records === null) return "";
      return records >= 0
        ? `Room for ${this.plural(people, "person", "people")} and ${this.plural(records, "record", "records")}.`
        : `Room for ${this.plural(people, "person", "people")}.`;
    },
    plural(count, one, many) {
      return `${this.number(count)} more ${count === 1 ? one : many}`;
    },
    decorate(p) {
      const current = String(p.planKey).toLowerCase() === String(this.currentKey).toLowerCase();
      const over = this.overFor(p);
      const monthly = this.money(p.priceMonthly);
      return {
        planKey: p.planKey,
        raw: p,
        name: this.planName(p.planKey),
        current,
        sellable: !!p.sellable,
        yearly: !!p.sellable && Number(p.priceYearly) > 0,
        sellableOrFree: p.sellable || String(p.planKey).toLowerCase() === "free",
        overBy: over,
        price: p.sellable ? (monthly || "—") : (this.rank(p.planKey) === 0 ? this.money(0) || "Free" : "—"),
        per: p.sellable && monthly ? "a month" : (this.rank(p.planKey) === 0 ? "always" : "not sold online"),
        fit: over.length
          ? { tone: "warn", text: `Over on ${this.listOf(over)}` }
          : { tone: "ok", text: this.headroom({ raw: p }) || "Fits" },
        feats: [
          { label: "People", value: p.includedMembers != null ? this.number(p.includedMembers) : "Custom" },
          { label: "Actor seats", value: this.seatLine(p) },
          { label: "Active records", value: p.activeRecords != null ? this.number(p.activeRecords) : "Custom" },
          { label: "File storage", value: p.storageGb != null ? `${this.number(p.storageGb)} GB` : "Custom" },
          { label: "Change history", value: this.historyLine(p.historyDays) },
          { label: "Studio", value: p.studioAccess ? "Included" : "Not included" },
        ],
      };
    },
    seatLine(p) {
      const included = p.includedActorSeats != null ? p.includedActorSeats : 1;
      if (p.addOnSlots == null) return `${included} included`;
      return `${included} + ${p.addOnSlots} add-on`;
    },
    historyLine(days) {
      if (!days) return "Unlimited";
      if (days < 360) return `${days} days`;
      const years = Math.round(days / 365);
      return years === 1 ? "1 year" : `${years} years`;
    },
    /**
     * One ruler row. Each rung owns an equal slice of the track and the marker is placed inside
     * the slice of the smallest rung that covers the usage, proportionally within it.
     */
    row(key, label, icon, used, pick, format) {
      const steps = this.plans.map(p => pick(p));
      if (!steps.some(v => v != null)) return null;
      const slice = 100 / this.plans.length;
      let index = steps.findIndex(v => v != null && used <= v);
      if (index < 0) index = this.plans.length - 1;
      const floor = index === 0 ? 0 : (steps[index - 1] || 0);
      const ceiling = steps[index] != null ? steps[index] : Math.max(used, floor + 1);
      const within = ceiling > floor ? Math.min(1, (used - floor) / (ceiling - floor)) : 1;
      return {
        key,
        label,
        icon,
        used: format(used),
        fill: `${(index * slice + within * slice).toFixed(1)}%`,
        ticks: steps.map(v => (v == null ? "Custom" : format(v))),
      };
    },
  },
};
</script>
